import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import { openWhatsApp } from '../utils/whatsapp';
import { useCart } from '../context/CartContext';
import '../styles/ProductDetail.css';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  useEffect(() => {
    // Forzar scroll con múltiples métodos
    const forceScrollToTop = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
    
    // Scroll inmediato
    forceScrollToTop();
    
    // Cargar producto y forzar scroll después
    loadProduct().then(() => {
      setTimeout(forceScrollToTop, 10);
      setTimeout(forceScrollToTop, 100);
    });
  }, [id]);

  const loadProduct = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await apiClient.get(`/ecommerce/product/${id}`);
      setProduct(data);
    } catch (err) {
      setError('No se pudo cargar el producto');
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsAppClick = () => {
    openWhatsApp(product);
  };

  const handleAddToCart = () => {
    addToCart(product);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 2
    }).format(price);
  };

  // Obtener array de imágenes del producto
  const getProductImages = () => {
    // Primero intentar con el array de imágenes
    if (product.imagenes && Array.isArray(product.imagenes) && product.imagenes.length > 0) {
      // Filtrar solo las URLs válidas (no vacías)
      const validImages = product.imagenes.filter(img => img && img.trim() !== '');
      if (validImages.length > 0) {
        return validImages;
      }
    }
    
    // Backward compatibility: si solo tiene la propiedad imagen
    if (product.imagen && product.imagen.trim() !== '') {
      return [product.imagen];
    }
    
    // Si no hay imágenes válidas, devolver placeholder
    return ['https://placehold.co/800x600?text=Sin+Imagen'];
  };

  const productImages = product ? getProductImages() : [];
  const selectedImage = productImages[selectedImageIndex] || productImages[0];
  
  // Verificar si el producto tiene precio válido
  const hasValidPrice = !!(product && product.precio && product.precio > 0);

  if (loading) {
    return (
      <div className="product-detail-loading">
        <div className="spinner"></div>
        <p>Cargando producto...</p>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="product-detail-error">
        <svg width="48" height="48" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <h3>Producto no encontrado</h3>
        <p>{error}</p>
        <button className="btn btn-primary" onClick={() => navigate('/')}>
          Volver al inicio
        </button>
      </div>
    );
  }

  return (
    <div className="product-detail-container">
      <button className="btn-back" onClick={() => navigate(-1)}>
        <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        Volver
      </button>

      <div className="product-detail-content">
        <div className="product-detail-image-section">
          {/* Imagen Principal */}
          <div className="product-detail-image-main">
            <img 
              src={selectedImage} 
              alt={`${product.descripcion} - Imagen ${selectedImageIndex + 1}`}
              className="product-detail-image"
              onError={(e) => {
                e.target.src = 'https://placehold.co/800x600?text=Sin+Imagen';
              }}
            />
            
            {/* Indicador de imagen actual */}
            {productImages.length > 1 && (
              <div className="image-counter">
                {selectedImageIndex + 1} / {productImages.length}
              </div>
            )}
          </div>

          {/* Galería de miniaturas */}
          {productImages.length > 1 && (
            <div className="product-detail-thumbnails">
              {productImages.map((image, index) => (
                <button
                  key={index}
                  className={`thumbnail ${selectedImageIndex === index ? 'active' : ''}`}
                  onClick={() => setSelectedImageIndex(index)}
                >
                  <img 
                    src={image} 
                    alt={`${product.descripcion} - Miniatura ${index + 1}`}
                    onError={(e) => {
                      e.target.src = 'https://placehold.co/100x100?text=Sin+Imagen';
                    }}
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="product-detail-info-section">
          {product.categoria && (
            <span className="product-detail-category">{product.categoria}</span>
          )}
          
          <h1 className="product-detail-title">{product.descripcion}</h1>
          
          <div className="product-detail-price-container">
            {hasValidPrice ? (
              <span className="product-detail-price">{formatPrice(product.precio)}</span>
            ) : (
              <span className="product-detail-price" style={{ color: '#666', fontStyle: 'italic' }}>
                Consultar precio
              </span>
            )}
            {product.existencia > 0 ? (
              <span className="badge badge-success">
                Disponible
              </span>
            ) : (
              <span className="badge badge-error">
                Sin Stock
              </span>
            )}
          </div>

          {product.descripcion_larga && (
            <div className="product-detail-description">
              <h3>Descripción</h3>
              <p>{product.descripcion_larga}</p>
            </div>
          )}

          {product.descripcion_completa && (
            <div className="product-detail-description">
              <h3>Descripción Completa</h3>
              <p style={{ whiteSpace: 'pre-wrap' }}>{product.descripcion_completa}</p>
            </div>
          )}

          <div className="product-detail-features">
            <div className="product-feature">
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Asesoramiento personalizado</span>
            </div>
            <div className="product-feature">
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Respuesta inmediata por WhatsApp</span>
            </div>
            <div className="product-feature">
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Información adicional disponible</span>
            </div>
          </div>

          <div className="product-detail-actions">
            {hasValidPrice && (
              <button 
                className="btn btn-primary btn-add-to-cart-large"
                onClick={handleAddToCart}
                disabled={!product.existencia || product.existencia === 0}
              >
                🛒 Agregar al Carrito
              </button>
            )}
            
            <button 
              className="btn btn-whatsapp btn-whatsapp-large"
              onClick={handleWhatsAppClick}
              style={!hasValidPrice ? { width: '100%' } : undefined}
            >
              <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              {!hasValidPrice ? 'Consultar por WhatsApp' : (product.existencia && product.existencia > 0 ? 'Consultar por WhatsApp' : 'Consultar disponibilidad')}
            </button>
          </div>

          {(!product.existencia || product.existencia === 0) && (
            <div className="product-detail-notice">
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p>Este producto está temporalmente sin stock. Contáctanos por WhatsApp para consultar disponibilidad y tiempos de entrega.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
